import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatPeriod } from '../../../lib/format/dates.ts';
import { formatDayNear } from '../../../lib/format/near.ts';
import { api, type TodoItem } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy } from '../../copy/en-CA.ts';
import { describeTodoFailure } from '../../describe.ts';
import { FROM_ADMIN } from '../../nav.ts';
import { useSession } from '../../session.tsx';

const c = copy.admin.todo;

/**
 * The Admin tab's first page (owner, 24 October 2026): only what is waiting
 * for an admin, each in plain words with a link to where it is done, or a
 * calm "Nothing waiting". What counts is decided on the server by the pure
 * module src/lib/admin/todo.ts; this page only words it. A list that could
 * not be loaded says so, never "Nothing waiting".
 */
export function AdminTodoPage() {
  const { user } = useSession();
  const [list, setList] = useState<{ items: TodoItem[]; today: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.admin
      .todo()
      .then((r) => {
        if (!cancelled) setList(r);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeTodoFailure(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>
      {error ? <Notice kind="error">{error}</Notice> : null}
      {!error && !list ? <p className="muted">{c.loading}</p> : null}
      {list?.items.length === 0 ? (
        <section className="card todo-empty">
          <p className="empty">
            <strong>{c.nothing}</strong>
            <span className="muted small">{c.nothingDetail}</span>
          </p>
        </section>
      ) : null}
      {list && list.items.length > 0 ? (
        <section className="card">
          <ul className="todo-list">
            {list.items.map((item) => (
              <li key={itemKey(item)}>
                <TodoLine item={item} today={list.today} you={user?.physicianId ?? null} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function itemKey(item: TodoItem): string {
  switch (item.kind) {
    case 'pending-requests':
    case 'not-answered':
      return `${item.kind}-${item.periodId}`;
    case 'draft':
    case 'flagged-weeks':
      return `${item.kind}-${item.scheduleId}`;
    case 'next-schedule':
    case 'holiday-check':
      return item.kind;
  }
}

/** One item: a small label of its kind, what is waiting, and the links to where it is done. */
function Line({ kind, text, notes, actions }: { kind: TodoItem['kind']; text: ReactNode; notes?: ReactNode; actions: ReactNode }) {
  return (
    <>
      <div className="todo-text">
        <span className="todo-kind">{c.kinds[kind]}</span>
        <p>{text}</p>
        {notes}
      </div>
      <div className="todo-actions">{actions}</div>
    </>
  );
}

function TodoLine({ item, today, you }: { item: TodoItem; today: string; you: string | null }) {
  switch (item.kind) {
    case 'pending-requests':
      return (
        <Line
          kind={item.kind}
          text={c.pending(item.count, item.periodName)}
          actions={
            // Availability selects the open period by itself; it cannot yet be told which one to review.
            <Link className="btn small" to="/admin/availability" aria-label={c.reviewLabel(item.periodName)}>
              {c.review}
            </Link>
          }
        />
      );
    case 'not-answered': {
      const cannot = item.physicians.filter((p) => p.account !== 'active');
      const mine = you !== null && item.physicians.some((p) => p.id === you);
      return (
        <Line
          kind={item.kind}
          text={
            <>
              {c.notAnswered(item.periodName, formatDayNear(item.dueDate, today))} {item.physicians.map((p) => (p.id === you ? c.you(p.name) : p.name)).join(', ')}
            </>
          }
          notes={
            cannot.length > 0 ? (
              <ul className="todo-notes">
                {cannot.map((p) => (
                  <li key={p.id}>
                    <span>{p.account === 'none' ? c.noAccount(p.name) : c.deactivated(p.name)}</span>
                    <Link to="/admin/users" aria-label={c.usersLabel(p.name)}>
                      {c.users}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : undefined
          }
          actions={
            <>
              {mine ? (
                <Link className="btn small" to={`/requests/${item.periodId}`}>
                  {c.answerYourself}
                </Link>
              ) : null}
              <Link className="btn small" to="/admin/availability" aria-label={c.availabilityLabel(item.periodName)}>
                {c.availability}
              </Link>
            </>
          }
        />
      );
    }
    case 'draft':
      return (
        <Line
          kind={item.kind}
          text={c.draft(item.name, formatPeriod(item.startDate, item.endDate), formatDayNear(item.createdOn, today))}
          actions={
            <Link className="btn small" to={`/schedules/${item.scheduleId}`} state={FROM_ADMIN} aria-label={c.openLabel(item.name)}>
              {c.open}
            </Link>
          }
        />
      );
    case 'next-schedule':
      return (
        <Line
          kind={item.kind}
          text={item.lastEnd === null ? c.firstSchedule : item.ended ? c.nextScheduleEnded(formatDayNear(item.lastEnd, today)) : c.nextSchedule(formatDayNear(item.lastEnd, today))}
          actions={
            <Link className="btn small" to="/admin/build">
              {item.lastEnd === null ? c.buildFirst : c.build}
            </Link>
          }
        />
      );
    case 'flagged-weeks':
      return (
        <Line
          kind={item.kind}
          text={c.flagged(item.count, item.name)}
          actions={
            <Link className="btn small" to={`/schedules/${item.scheduleId}`} state={FROM_ADMIN} aria-label={c.openLabel(item.name)}>
              {c.open}
            </Link>
          }
        />
      );
    case 'holiday-check':
      return (
        <Line
          kind={item.kind}
          text={item.problem === 'disagrees' ? c.holidayDisagrees : c.holidayFailing}
          actions={
            <Link className="btn small" to="/admin/build" aria-label={c.holidayLabel}>
              {c.holidayLink}
            </Link>
          }
        />
      );
  }
}
